/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Autocomplete, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  FormGroup, Link, MenuItem, Stack, TextField, Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { emailTemplatesAPI, emailsAPI, filesAPI } from '../../api/crm'
import { useProfile, useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'
import { allFields } from './fieldValues'
import { formatSize } from './RecordFiles'

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const MAX_ATTACH = 15 * 1024 * 1024

/** Addresses as chips: type and press Enter, comma or space; the record's emails are offered. */
const AddressInput = ({ label, value, onChange, options, autoFocus }) => {
  const [text, setText] = useState('')
  const add = (raw) => {
    const parts = raw.split(/[\s,;]+/).map((p) => p.trim()).filter(Boolean)
    if (parts.length) onChange([...new Set([...value, ...parts])])
    setText('')
  }
  return (
    <Autocomplete
      multiple freeSolo options={options.filter((o) => !value.includes(o))} value={value} inputValue={text}
      onInputChange={(_, v, reason) => { if (reason === 'input') { if (/[,;\s]$/.test(v)) add(v); else setText(v) } }}
      onChange={(_, next) => onChange(next.map((v) => v.trim()).filter(Boolean))}
      onBlur={() => text.trim() && add(text)}
      renderTags={(items, getTagProps) => items.map((v, i) => (
        <Chip {...getTagProps({ index: i })} key={v} label={v} size="small" color={EMAIL.test(v) ? 'default' : 'error'} variant="outlined" />
      ))}
      renderInput={(params) => <TextField {...params} label={label} autoFocus={autoFocus} />}
    />
  )
}

/**
 * Compose an email from a record. It's sent from the CRM's address in the member's name, replies go
 * to the member, and it's logged on the record's timeline (also when the mail server refuses it).
 */
const EmailDialog = ({ open, onClose, record, form }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const { data: profile } = useProfile()
  const recordEmails = [...new Set(allFields(form).filter((f) => f.fieldType === 'EMAIL')
    .map((f) => record.data[f.fieldKey]).filter((v) => typeof v === 'string' && v))]
  const [to, setTo] = useState(recordEmails.slice(0, 1))
  const [cc, setCc] = useState([])
  const [showCc, setShowCc] = useState(false)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [attach, setAttach] = useState([])
  const { data: files = [] } = useQuery({ queryKey: [tenantId, 'records', 'files', record.id], queryFn: () => filesAPI.list(record.id) })
  const { data: templates = [] } = useQuery({ queryKey: [tenantId, 'email-templates', record.entityType], queryFn: () => emailTemplatesAPI.list(record.entityType) })
  const [templateError, setTemplateError] = useState(null)
  const applyTemplate = async (id) => {
    if (!id) return
    try {
      const filled = await emailTemplatesAPI.render(id, record.id)
      setSubject(filled.subject)
      setBody(filled.body)
      setTemplateError(null)
    } catch (e) {
      setTemplateError(getErrorMessage(e))
    }
  }
  const attachBytes = files.filter((f) => attach.includes(f.id)).reduce((sum, f) => sum + f.sizeBytes, 0)
  const invalid = [...to, ...cc].filter((a) => !EMAIL.test(a))

  const send = useMutation({
    mutationFn: () => emailsAPI.send(record.id, { to, cc, subject: subject.trim(), body, attachmentIds: attach }),
    onSuccess: (email) => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'records', 'activity', record.id] })
      if (email.status === 'SENT') {
        notify(`Email sent to ${email.to.join(', ')}`)
        onClose()
      }
    },
  })
  const failed = send.data?.status === 'FAILED'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => {
        // dialogs render in a portal, but React still bubbles submit to an enclosing form (the record page)
        e.preventDefault(); e.stopPropagation(); send.mutate()
      } }}>
      <DialogTitle>Email from {record.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {send.error && <Alert severity="error">{getErrorMessage(send.error)}</Alert>}
          {failed && <Alert severity="error">Not sent: {send.data.error}. It’s on the record’s timeline; check the address or try again later.</Alert>}
          <AddressInput label="To" value={to} onChange={setTo} options={recordEmails} autoFocus={to.length === 0} />
          {showCc
            ? <AddressInput label="Cc" value={cc} onChange={setCc} options={recordEmails} />
            : <Link component="button" type="button" variant="body2" sx={{ alignSelf: 'flex-start' }} onClick={() => setShowCc(true)}>Add Cc</Link>}
          {invalid.length > 0 && <Typography variant="caption" color="error">Not an email address: {invalid.join(', ')}</Typography>}
          {templates.length > 0 && (
            <TextField select size="small" label="Start from a template" value="" onChange={(e) => applyTemplate(e.target.value)} sx={{ maxWidth: 360 }}
              helperText={templateError || 'Fills in the subject and message; you can still edit them'} error={!!templateError}>
              {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </TextField>
          )}
          <TextField label="Subject" required value={subject} onChange={(e) => setSubject(e.target.value)} inputProps={{ maxLength: 300 }} autoFocus={to.length > 0} />
          <TextField label="Message" required multiline minRows={8} maxRows={20} value={body} onChange={(e) => setBody(e.target.value)} inputProps={{ maxLength: 20000 }} />
          {files.length > 0 && (
            <div>
              <Typography variant="subtitle2">Attach files from this record</Typography>
              <FormGroup>
                {files.map((f) => (
                  <FormControlLabel key={f.id} label={<Typography variant="body2">{f.fileName} <Typography component="span" variant="caption" color="text.secondary">{formatSize(f.sizeBytes)}</Typography></Typography>}
                    control={<Checkbox size="small" checked={attach.includes(f.id)} onChange={(e) => setAttach((a) => (e.target.checked ? [...a, f.id] : a.filter((x) => x !== f.id)))} />} />
                ))}
              </FormGroup>
              {attachBytes > MAX_ATTACH && <Typography variant="caption" color="error">Attachments can be at most 15 MB together.</Typography>}
            </div>
          )}
          <Typography variant="caption" color="text.secondary">
            Sent as {profile?.displayName || profile?.email || 'you'} from the CRM’s address; replies go to {profile?.email || 'your email'}.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{failed ? 'Close' : 'Cancel'}</Button>
        <Button type="submit" variant="contained"
          disabled={send.isPending || to.length === 0 || invalid.length > 0 || !subject.trim() || !body.trim() || attachBytes > MAX_ATTACH}>
          {send.isPending ? 'Sending…' : failed ? 'Try again' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EmailDialog
